import { useEffect, useState } from "react";
import { Form, Table, Alert } from "react-bootstrap";
import {
  organizerCreateCouponApi,
  organizerListCouponsApi,
  organizerUpdateCouponApi,
  organizerListEventsApi,
} from "../../api/api";
import { handleError } from "../../helpers/handleError";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import OrganizerPillSelect from "../components/OrganizerPillSelect";
import "../dashboard.css";

const Coupons = () => {
  const [events, setEvents] = useState([]);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ code: "", discount: 0, eventid: "", status: true, discountType: "fixed" });
  const [editingId, setEditingId] = useState(null);
  const [editRow, setEditRow] = useState({ code: "", discount: 0, status: true, eventid: "", discountType: "fixed" });

  const load = async () => {
    setLoading(true);
    try {
      const [ev, cp] = await Promise.all([organizerListEventsApi(), organizerListCouponsApi()]);
      setEvents(Array.isArray(ev?.data?.info) ? ev.data.info : []);
      setList(Array.isArray(cp?.data?.info) ? cp.data.info : []);
    } catch (e) {
      handleError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    setError("");
    try {
      setLoading(true);
      const payload = { code: form.code, discount: Number(form.discount), discountType: form.discountType };
      if (form.eventid) payload.eventid = form.eventid;
      if (form.status !== undefined) payload.status = form.status;
      await organizerCreateCouponApi(payload);
      setForm({ code: "", discount: 0, eventid: "", status: true, discountType: "fixed" });
      await load();
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || "Error al crear cupón");
    } finally {
      setLoading(false);
    }
  };

  const onStartEdit = (c) => {
    setEditingId(c._id);
    setEditRow({
      code: c.code,
      discount: c.discount,
      status: c.status,
      eventid: c.eventid || "",
      discountType: c.discountType || "fixed",
    });
  };

  const onSaveEdit = async (id) => {
    try {
      setLoading(true);
      await organizerUpdateCouponApi({ id, ...editRow });
      setEditingId(null);
      await load();
    } catch (err) {
      handleError(err);
      alert(err?.response?.data?.message || "Error actualizando cupón");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <SpinLoading />;

  return (
    <div className="organizer-page">
      <section className="organizer-section">
      <div className="organizer-section__header">
        <div>
          <p className="organizer-eyebrow">Promociones</p>
          <h2>Cupones</h2>
          <p className="organizer-section__subtitle">Generá códigos de descuento para tus campañas.</p>
        </div>
      </div>
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      <div className="organizer-form-grid">
        <div className="organizer-form-card">
          <h3>Crear nuevo cupón</h3>
          <Form onSubmit={onCreate}>
            <Form.Control
              className="organizer-field"
              placeholder="Código"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              required
            />
            <Form.Control
              className="organizer-field"
              placeholder="Descuento"
              type="number"
              value={form.discount}
              onChange={(e) => setForm({ ...form, discount: e.target.value })}
              required
            />
            <Form.Select
              className="organizer-field organizer-select"
              value={form.discountType}
              onChange={(e) => setForm({ ...form, discountType: e.target.value })}
            >
              <option value="fixed">Monto fijo</option>
              <option value="percent">Porcentaje %</option>
            </Form.Select>
            <OrganizerPillSelect
              placeholder="Selecciona evento"
              value={form.eventid}
              onChange={(val) => setForm({ ...form, eventid: val ? String(val) : "" })}
              options={events
                .map((ev) => {
                  const raw = ev?.id ?? ev?._id ?? ev?.eventid;
                  if (!raw) return null;
                  return { value: String(raw), label: ev?.name || "Evento sin nombre" };
                })
                .filter(Boolean)}
            />
            <Form.Check
              className="mb-2"
              type="switch"
              id="coupon-status"
              label={form.status ? "Activo" : "Inactivo"}
              checked={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.checked })}
            />
            <div className="organizer-form-actions">
              <button type="submit" className="organizer-btn">
                Crear
              </button>
            </div>
          </Form>
        </div>
      </div>

      <div className="organizer-card">
        <div className="organizer-card__header">
          <div>
            <h3 className="organizer-card__title">Listado de cupones</h3>
            <p className="organizer-card__subtitle">Editá valores y estado directamente desde la tabla.</p>
          </div>
          <div className="organizer-chip organizer-chip--secondary">{list.length} cupones</div>
        </div>
        <div className="organizer-table">
          <Table responsive className="organizer-table__inner align-middle">
            <thead>
              <tr>
                <th>Código</th>
                <th>Descuento</th>
                <th>Tipo</th>
                <th>Evento</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c._id}>
                  <td>
                    {editingId === c._id ? (
                      <Form.Control value={editRow.code} onChange={(e) => setEditRow({ ...editRow, code: e.target.value })} />
                    ) : (
                      c.code
                    )}
                  </td>
                  <td>
                    {editingId === c._id ? (
                      <Form.Control type="number" value={editRow.discount} onChange={(e) => setEditRow({ ...editRow, discount: e.target.value })} />
                    ) : (
                      c.discount
                    )}
                  </td>
                  <td>
                    {editingId === c._id ? (
                      <Form.Select className="organizer-select" value={editRow.discountType} onChange={(e) => setEditRow({ ...editRow, discountType: e.target.value })}>
                        <option value="fixed">Fijo</option>
                        <option value="percent">Porcentaje</option>
                      </Form.Select>
                    ) : (
                      c.discountType || "fixed"
                    )}
                  </td>
                  <td>
                    {editingId === c._id ? (
                      <Form.Select className="organizer-select" value={editRow.eventid || ""} onChange={(e) => setEditRow({ ...editRow, eventid: e.target.value })}>
                        <option value="">Ninguno</option>
                        {events.map((ev) => (
                          <option key={ev.id} value={ev.id}>
                            {ev.name}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      (() => {
                        const ev = events.find((e) => e.id === (c.eventid?._id || c.eventid) || e.id === c.eventid);
                        return ev ? ev.name : "-";
                      })()
                    )}
                  </td>
                  <td>
                    {editingId === c._id ? (
                      <Form.Check type="switch" checked={editRow.status} onChange={(e) => setEditRow({ ...editRow, status: e.target.checked })} />
                    ) : (
                      <span
                        className={`organizer-chip ${c.status ? "organizer-chip--success" : "organizer-chip--secondary"}`}
                      >
                        {c.status ? "Activo" : "Inactivo"}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingId === c._id ? (
                      <div className="organizer-form-actions">
                        <button type="button" className="organizer-btn" onClick={() => onSaveEdit(c._id)}>
                          Guardar
                        </button>
                        <button type="button" className="organizer-btn secondary" onClick={() => setEditingId(null)}>
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <button type="button" className="organizer-link-btn" onClick={() => onStartEdit(c)}>
                        Editar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-3">
                    Sin cupones
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
      </section>
    </div>
  );
};

export default Coupons;
